import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, Router } from '@angular/router';
import { AuthState } from 'app/core/state/auth.state';

/**
 * Guard that blocks users without analytics access from accessing analytics routes.
 *
 * ROLE ACCESS:
 * - OrganizationAdmin: ✅
 * - AnalyticsAdmin: ✅
 * - Development: ✅
 * - Demo: ✅
 * - SuperAdmin: ✅ (when admin context is set)
 * - SystemAdmin: ✅ (when admin context is set)
 * - No roles: ❌ → redirect to /unauthorized
 */
export const AnalyticsGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router = inject(Router);
  const authState = inject(AuthState);

  // Allow if user has standard analytics access
  if (authState.hasAnalyticsAccess()) {
    return true;
  }

  // Also allow SuperAdmin/SystemAdmin when they have admin context set
  // This lets admins view dashboards after selecting a context
  if (authState.hasAdminContext()) {
    return true;
  }

  // SuperAdmin/SystemAdmin without context → redirect to select context first
  const roles = authState.user()?.roles ?? [];
  if (roles.includes('SuperAdmin') || roles.includes('SystemAdmin')) {
    console.log('[AnalyticsGuard] Admin without context - redirecting to select-context');
    router.navigate(['/admin/select-context'], {
      queryParams: { returnUrl: state.url }
    });
    return false;
  }

  console.log('[AnalyticsGuard] Access denied - redirecting to:', authState.defaultRoute());
  router.navigate([authState.defaultRoute()]);
  return false;
};
